import type {
  JobWithArtifacts,
  JobInput,
  JobRun,
  Settings,
  ConnectionStatus,
  CurrentUser,
  ArtifactType,
} from "@shared/types";
import { getAccessToken } from "../lib/session.ts";

// ---------------------------------------------------------------------------
// Typed API client. Attaches the Supabase session token when present; in dev
// (no token) the backend authenticates via its bypass.
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg = (data && (data.error as string)) || `Request failed (${res.status})`;
    throw new ApiError(res.status, msg, data?.details);
  }
  return data as T;
}

export const api = {
  me: () => request<CurrentUser>("/api/me"),

  listJobs: () => request<JobWithArtifacts[]>("/api/jobs"),
  getJob: (id: string) => request<JobWithArtifacts>(`/api/jobs/${id}`),
  createJob: (input: JobInput) =>
    request<JobWithArtifacts>("/api/jobs", { method: "POST", body: JSON.stringify(input) }),
  runJob: (id: string) => request<JobWithArtifacts>(`/api/jobs/${id}/run`, { method: "POST" }),
  retryStep: (id: string, type: ArtifactType) =>
    request<JobWithArtifacts>(`/api/jobs/${id}/steps/${type}/retry`, { method: "POST" }),
  patchJob: (id: string, patch: Record<string, unknown>) =>
    request<JobWithArtifacts>(`/api/jobs/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  getRuns: (id: string) => request<JobRun[]>(`/api/jobs/${id}/runs`),

  getSettings: () => request<Settings>("/api/settings"),
  updateSettings: (settings: Settings) =>
    request<Settings>("/api/settings", { method: "PUT", body: JSON.stringify(settings) }),

  getConnections: () =>
    request<{ google: ConnectionStatus; docusign: ConnectionStatus }>("/api/connections"),
  getConnectUrl: (provider: "google" | "docusign") =>
    request<{ url: string }>(`/api/connections/${provider}/start`),
  disconnect: (provider: "google" | "docusign") =>
    request<{ ok: boolean }>(`/api/connections/${provider}/disconnect`, { method: "POST" }),
};
